import Link from 'found/Link';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  Card, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import getFullAddressString from 'src/util/address';

import AdminPropertyShowMeter from './AdminPropertyShowMeter';
import AdminPropertyShowPrimaryBillingPoint from './AdminPropertyShowPrimaryBillingPoint';
import AdminPropertyShowPropertyUser from './AdminPropertyShowPropertyUser';
import AdminPropertyShowTariff from './AdminPropertyShowTariff';

/**
 * Description
 * @param {object} props
 * @param {object} props.property
 * @returns {React.ReactComponentElement} - AdminPropertyShow component
 */
function AdminPropertyShow({ property = null }) {
  const intl = useIntl();

  if (!property) {
    return <Loading />;
  }

  const {
    uuid, externalIdentifier, title, address, active, createdAt, updatedAt, propertyUsers,
    primaryBillingPoints, meters, tariffs, community,
  } = property;
  const { start, finish } = active;
  const addressString = getFullAddressString(address);

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.page.title', defaultMessage: 'Enosi Admin - Property - {title}' },
    { title },
  );
  const breadcrumbsAdmin = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.breadcrumbs.admin.label', defaultMessage: 'Admin' },
  );
  const breadcrumbsProperties = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.breadcrumbs.properties.label', defaultMessage: 'Properties' },
  );

  // Card headers
  const propertyDetailsHeader = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.card.property_details.header', defaultMessage: 'Property details' },
  );
  const propertyUsersHeader = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.card.property_users.header', defaultMessage: 'Property Users' },
  );
  const primaryBillingPointsHeader = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.card.primary_billing_points.header', defaultMessage: 'Primary Billing Points' },
  );
  const metersHeader = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.card.meters.header', defaultMessage: 'Meters' },
  );
  const tariffsHeader = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.card.tariffs.header', defaultMessage: 'Tariffs' },
  );

  // Table column headers
  const tableLinksLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.links.label', defaultMessage: 'Links' },
  );
  const tableCommunityLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.community.label', defaultMessage: 'Community' },
  );
  const tableCustomerPageLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.links.customer_page', defaultMessage: 'Customer Page' },
  );
  const tableIdLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.id.label', defaultMessage: 'ID' },
  );
  const tableExternalIdentifierLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.external_identifier.label', defaultMessage: 'External identifier' },
  );
  const tableActiveStatusLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.active_status.label', defaultMessage: 'Active status' },
  );
  const tableTitleLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.title.label', defaultMessage: 'Title' },
  );
  const tableAddressLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.address.label', defaultMessage: 'Address' },
  );
  const tableStartLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.start.label', defaultMessage: 'Start' },
  );
  const tableFinishLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.finish.label', defaultMessage: 'Finish' },
  );
  const tableCreatedAtLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.created_at.label', defaultMessage: 'Created at' },
  );
  const tableUpdatedAtLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.updated_at.label', defaultMessage: 'Updated at' },
  );

  // Table abbreviations and tooltips
  const tableIdentifierTitle = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.identifier.title', defaultMessage: 'Identifier' },
  );
  const tableIdAbbr = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.id.abbr', defaultMessage: 'ID' },
  );
  const tableExtIdAbbr = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.ext_id.abbr', defaultMessage: 'Ext ID' },
  );
  const tableActiveLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.active.label', defaultMessage: 'Active' },
  );
  const tableUsernameLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.username.label', defaultMessage: 'Username' },
  );
  const tableEmailLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.email.label', defaultMessage: 'Email' },
  );
  const tablePropertyUserLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.property_user.label', defaultMessage: 'PropertyUser' },
  );
  const tableUserLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.user.label', defaultMessage: 'User' },
  );
  const tablePrimaryBillingPointLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.primary_billing_point.label', defaultMessage: 'Primary Billing Point' },
  );
  const tableMeterLabel = intl.formatMessage(
    { id: 'admin.admin_property.admin_property_show.table.meter.label', defaultMessage: 'Meter' },
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbsAdmin, path: '/admin' },
          { name: breadcrumbsProperties, path: '/admin/properties' },
          { name: title },
        ]}
      />

      <h1>
        {title}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card>
            <CardHeader>
              {propertyDetailsHeader}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{tableLinksLabel}</th>
                  <td>
                    <Link to={`/properties/${uuid}`}>
                      {tableCustomerPageLabel}
                    </Link>
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableCommunityLabel}</th>
                  <td>
                    <Link to={`/admin/communities/${community.uuid}`}>
                      {community.title}
                    </Link>
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableIdLabel}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableExternalIdentifierLabel}</th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableActiveStatusLabel}</th>
                  <td>
                    <BadgeActive start={start} finish={finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableTitleLabel}</th>
                  <td>{title}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableAddressLabel}</th>
                  <td>{addressString}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableStartLabel}</th>
                  <td>
                    {start && DateTime.fromSeconds(start).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableFinishLabel}</th>
                  <td>
                    {finish && DateTime.fromSeconds(finish).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableCreatedAtLabel}</th>
                  <td>
                    {createdAt
                      && DateTime.fromSeconds(createdAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableUpdatedAtLabel}</th>
                  <td>
                    {updatedAt
                      && DateTime.fromSeconds(updatedAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card className="mb-4">
            <CardHeader>
              {propertyUsersHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col" colSpan={3} className="text-center">{tablePropertyUserLabel}</th>
                  <th scope="col" colSpan={5} className="text-center">{tableUserLabel}</th>
                </tr>
                <tr>
                  <th scope="col">
                    <abbr title={tableIdentifierTitle}>{tableIdAbbr}</abbr>
                  </th>
                  <th scope="col">{tableActiveLabel}</th>
                  <th scope="col">
                    <abbr title={tableExternalIdentifierLabel}>{tableExtIdAbbr}</abbr>
                  </th>
                  <th scope="col">{tableUsernameLabel}</th>
                  <th scope="col">{tableEmailLabel}</th>
                  <th scope="col">{tableActiveLabel}</th>
                  <th scope="col">
                    <abbr title={tableIdentifierTitle}>{tableIdAbbr}</abbr>
                  </th>
                  <th scope="col">
                    <abbr title={tableExternalIdentifierLabel}>{tableExtIdAbbr}</abbr>
                  </th>
                </tr>
              </thead>
              <tbody>
                {propertyUsers && propertyUsers.edges.map((el) => (
                  <AdminPropertyShowPropertyUser
                    propertyUser={el.node}
                    intl={intl}
                    key={el.node.uuid}
                  />
                ))}
              </tbody>
            </Table>
          </Card>

          <Card className="mb-4">
            <CardHeader>
              {primaryBillingPointsHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{tableIdentifierTitle}</th>
                  <th scope="col">
                    <abbr title={tableIdentifierTitle}>{tableIdAbbr}</abbr>
                  </th>
                  <th scope="col">{tableActiveLabel}</th>
                </tr>
              </thead>
              <tbody>
                {primaryBillingPoints && primaryBillingPoints.edges.map((el) => (
                  <AdminPropertyShowPrimaryBillingPoint
                    primaryBillingPoint={el.node}
                    key={el.node.uuid}
                  />
                ))}
              </tbody>
            </Table>
          </Card>

          <Card className="mb-4">
            <CardHeader>
              {metersHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{tableIdentifierTitle}</th>
                  <th scope="col">
                    <abbr title={tableIdentifierTitle}>{tableIdAbbr}</abbr>
                  </th>
                  <th scope="col">{tableActiveLabel}</th>
                  <th scope="col">{tablePrimaryBillingPointLabel}</th>
                </tr>
              </thead>
              <tbody>
                {meters && meters.edges.map((el) => (
                  <AdminPropertyShowMeter meter={el.node} key={el.node.uuid} />
                ))}
              </tbody>
            </Table>
          </Card>

          <Card className="mb-4">
            <CardHeader>
              {tariffsHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{tableTitleLabel}</th>
                  <th scope="col">{tableIdentifierTitle}</th>
                  <th scope="col">
                    <abbr title={tableIdentifierTitle}>{tableIdAbbr}</abbr>
                  </th>
                  <th scope="col">{tableActiveLabel}</th>
                  <th scope="col">{tableMeterLabel}</th>
                </tr>
              </thead>
              <tbody>
                {tariffs && tariffs.edges.map((el) => (
                  <AdminPropertyShowTariff tariff={el.node} key={el.node.uuid} />
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminPropertyShow.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default createFragmentContainer(
  AdminPropertyShow,
  {
    property: graphql`
      fragment AdminPropertyShow_property on Property {
        uuid
        externalIdentifier
        title
        address {
          line1
          line2
          city
          state
          postcode
        }
        active { start finish }
        createdAt
        updatedAt
        community {
          title
          uuid
        }
        propertyUsers(first: 500) {
          edges {
            node {
              uuid
              externalIdentifier
              active { start finish }
              user {
                uuid
                externalIdentifier
                email
                givenName
                familyName
                active { start finish }
              }
            }
          }
        }
        primaryBillingPoints(first: 500) {
          edges {
            node {
              uuid
              identifier
              active { start finish }
            }
          }
        }
        meters(first: 500) {
          edges {
            node {
              uuid
              identifier
              active { start finish }
              primaryBillingPoint {
                uuid
                identifier
              }
            }
          }
        }
        tariffs(first: 500) {
          edges {
            node {
              uuid
              identifier
              title
              active { start finish }
              meter {
                uuid
                identifier
              }
            }
          }
        }
      }
    `,
  },
);
